import React from "react";
import { Pressable, View } from "react-native";
import { useI18n } from "@/context/I18nContext";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { SearchInput } from "@/components/ui/SearchInput";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { Icon } from "@/components/ui/Icon";
import { Feather, Ionicons } from "@expo/vector-icons";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import type {
  ContentType,
  LocalizationKey,
  LocalizationListResponse,
  SortField,
  StatusFilter,
} from "@/lib/api/localization";
import { LocalizationCard } from "./LocalizationCard";
import { EmptyState } from "@/components/ui/EmptyState";

export interface LocalizationListProps {
  data?: LocalizationListResponse;
  isLoading?: boolean;
  contentType: ContentType;
  onContentTypeChange: (type: ContentType) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (filter: StatusFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  sortField: SortField;
  onSortChange: (sort: SortField) => void;
  page: number;
  onPageChange: (page: number) => void;
  onAddContent: () => void;
  onCardPress: (item: LocalizationKey) => void;
}

// ─── Dropdown section configs ─────────────────────────────────────────────────

const FILTER_SECTIONS_CONFIG: {
  titleKey: TranslationKeys;
  items: { value: StatusFilter; labelKey: TranslationKeys }[];
}[] = [
  {
    titleKey: "staff.localization.filter.title",
    items: [
      {
        value: "all" as StatusFilter,
        labelKey: "staff.localization.filter.statusAll",
      },
      {
        value: "completed" as StatusFilter,
        labelKey: "staff.localization.filter.statusCompleted",
      },
      {
        value: "pending" as StatusFilter,
        labelKey: "staff.localization.filter.statusPending",
      },
    ],
  },
];

const SORT_SECTIONS_CONFIG: {
  titleKey: TranslationKeys;
  items: { value: SortField; labelKey: TranslationKeys }[];
}[] = [
  {
    titleKey: "staff.localization.sort.sectionAlpha",
    items: [
      {
        value: "alpha_asc" as SortField,
        labelKey: "staff.localization.sort.alphaAsc",
      },
      {
        value: "alpha_desc" as SortField,
        labelKey: "staff.localization.sort.alphaDesc",
      },
    ],
  },
  {
    titleKey: "staff.localization.sort.sectionDate",
    items: [
      {
        value: "last_edited" as SortField,
        labelKey: "staff.localization.sort.lastEdited",
      },
    ],
  },
];

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Content section of the Localization tab screen.
 *
 * Renders: content-type sub-tab bar, toolbar (search + Add Content + Filter + Sort),
 * 2-column card grid, numbered pagination row.
 *
 * Matches Figma "Staff / Localization / Content List" redesign.
 */
export function LocalizationList({
  data,
  isLoading,
  contentType,
  onContentTypeChange,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  sortField,
  onSortChange,
  page,
  onPageChange,
  onAddContent,
  onCardPress,
}: LocalizationListProps) {
  const { t, isRTL } = useI18n();

  // Translate label keys and section titles at render time
  const filterSections = FILTER_SECTIONS_CONFIG.map((s) => ({
    title: t(s.titleKey),
    items: s.items.map((item) => ({
      value: item.value,
      label: t(item.labelKey),
    })),
  }));

  const sortSections = SORT_SECTIONS_CONFIG.map((s) => ({
    title: t(s.titleKey),
    items: s.items.map((item) => ({
      value: item.value,
      label: t(item.labelKey),
    })),
  }));

  const categoriesCount = data?.categoriesCount ?? 0;
  const industriesCount = data?.industriesCount ?? 0;
  const otherCount = data?.otherCount ?? 0;
  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const pageSize = data?.pageSize ?? 10;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fromRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const toRow = Math.min(page * pageSize, total);

  // Pair items into rows of 2 for the 2-column grid
  const rows: [LocalizationKey, LocalizationKey | null][] = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push([items[i], items[i + 1] ?? null]);
  }

  // Generate page numbers to show (e.g. 1 2 … 4 for totalPages=4 on page 1)
  const pageNumbers = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | "ellipsis")[] = [1];
    if (page > 3) pages.push("ellipsis");
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) {
      pages.push(p);
    }
    if (page < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  })();

  return (
    <View className="w-full flex-col">
      {/* Content-type tab bar */}
      <TabBarNavigation<ContentType>
        tabs={[
          {
            key: "categories",
            label: t("staff.localization.tabs.categories"),
            count: categoriesCount,
          },
          {
            key: "industries",
            label: t("staff.localization.tabs.industries"),
            count: industriesCount,
          },
          {
            key: "other",
            label: t("staff.localization.tabs.other"),
            count: otherCount,
          },
        ]}
        activeTab={contentType}
        onTabPress={onContentTypeChange}
        contentContainerClassName="px-page-x tablet:px-20"
      />

      {/* Toolbar */}
      <View className="px-page-x tablet:px-20 py-4">
        <View className="gap-3 flex-wrap tablet:flex-row tablet:items-center justify-between">
          {/* Search */}
          <SearchInput
            containerClassName="max-w-sm w-full mb-0"
            value={search}
            onChangeText={onSearchChange}
            placeholder={"common.search"}
          />

          {/* Right side: Add Content + Filter + Sort */}
          <View className="flex-row flex-wrap items-center gap-3">
            <Button
              icon={<Icon as={Feather} name="plus" size={14} className="text-white" />}
              label={"staff.localization.actions.addContent"}
              onPress={onAddContent}
              variant="primary"
            />

            {/* Filter dropdown */}
            <DropdownMenu
              sections={filterSections}
              value={statusFilter}
              onSelect={onStatusFilterChange}
              panelWidth={176}
              testID="localization-filter"
              trigger={
                <View pointerEvents="none">
                  <Button
                    icon={
                      <Icon as={Feather} name="sliders" size={14} className="text-dark-text-20" />
                    }
                    label={"staff.localization.actions.filter"}
                    variant="ghost"
                    onPress={() => {}}
                  />
                </View>
              }
            />

            {/* Sort dropdown */}
            <DropdownMenu
              sections={sortSections}
              value={sortField}
              onSelect={onSortChange}
              panelWidth={208}
              testID="localization-sort"
              trigger={
                <View pointerEvents="none">
                  <Button
                    icon={
                      <Icon
                        as={Ionicons}
                        name="swap-vertical"
                        size={14}
                        className="text-dark-text-20"
                      />
                    }
                    label={"staff.localization.actions.sort"}
                    variant="ghost"
                    onPress={() => {}}
                  />
                </View>
              }
            />
          </View>
        </View>
      </View>

      {/* Card grid */}
      <View className="px-page-x tablet:px-20 pb-6">
        {isLoading ? (
          <View className="py-12 items-center">
            <AppText className="text-sm text-supporting-text">{t("common.loading")}</AppText>
          </View>
        ) : items.length === 0 ? (
          <EmptyState description="common.table.empty.description" />
        ) : (
          <View className="gap-6">
            {rows.map(([left, right], rowIdx) => (
              <View key={rowIdx} className="tablet:flex-row gap-6">
                <LocalizationCard item={left} onPress={onCardPress} />
                {right ? (
                  <LocalizationCard item={right} onPress={onCardPress} />
                ) : (
                  <View className="flex-1" />
                )}
              </View>
            ))}
          </View>
        )}

        {/* Pagination row */}
        {total > 0 && (
          <View className="flex-row flex-wrap gap-4 items-center justify-between mt-6 py-4 px-6 border rounded-md border-stroke-50">
            <AppText className="text-base text-supporting-text">
              {t("staff.localization.showing")
                .replace("{from}", String(fromRow))
                .replace("{to}", String(toRow))
                .replace("{total}", String(total))}
            </AppText>
            {/* Pagination control — single bordered container with vertical dividers */}
            <View className={"flex-row items-stretch rounded overflow-hidden"}>
              {/* Prev button */}
              <Pressable
                onPress={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className={`w-8 h-8 items-center justify-center border border-r-0 border-stroke-50 rounded-l-sm ${page <= 1 ? "opacity-40" : ""}`}
              >
                <Icon
                  as={Feather}
                  name={isRTL ? "chevron-right" : "chevron-left"}
                  size={14}
                  className="text-dark-text-30"
                />
              </Pressable>

              {pageNumbers.map((p, idx) => (
                <React.Fragment key={idx}>
                  {/* Vertical divider */}
                  <View className="w-px self-stretch bg-stroke-50" />
                  {p === "ellipsis" ? (
                    <View className="h-8 min-w-[32px] px-2 items-center justify-center">
                      <AppText weight="medium" className="text-base text-supporting-text">
                        ...
                      </AppText>
                    </View>
                  ) : (
                    <Pressable
                      onPress={() => onPageChange(p as number)}
                      className={`h-8 min-w-[32px] px-2 items-center justify-center border ${
                        p === page ? "bg-primary border-primary" : "bg-white border-stroke-50"
                      }`}
                    >
                      <AppText
                        weight="medium"
                        className={`text-base ${p === page ? "text-white" : "text-supporting-text"}`}
                      >
                        {String(p)}
                      </AppText>
                    </Pressable>
                  )}
                </React.Fragment>
              ))}

              {/* Vertical divider */}
              <View className="w-px self-stretch bg-stroke-50" />
              {/* Next button */}
              <Pressable
                onPress={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                testID="pagination-next"
                className={`w-8 h-8 items-center justify-center border border-l-0 border-stroke-50 rounded-r-sm  ${page >= totalPages ? "opacity-40" : ""}`}
              >
                <Icon
                  as={Feather}
                  name={isRTL ? "chevron-left" : "chevron-right"}
                  size={14}
                  className="text-dark-text-30"
                />
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}
